import {
  Button, Box, Grid2 as Grid, Stack, TextField, Typography
} from "@mui/material";
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import PropTypes from 'prop-types';

import { processPayment } from '../../actions/investments';
import { MODAL_MESSAGES } from "../../components/common/helpers";
import { isEmpty } from "../../components/common/helpers";
import GooglePayButton from './GooglePayButton';
import ApplePayButton from './ApplePayButton';

const FormInversion = ({
  errors, newInvestment, user, setLoading,
  handleChange, setShowModal, setModalContent, addInvestment, sendEmailApproval
}) => {
  const PAY_OPTIONS = [
    // {name: "card_mastercard", url: "/Mastercard.png" },
    // {name: "card_visa", url: "/VISA.png" },
    // {name: "card_amex", url: "/AMEX.png" },
    { name: "paypal", url: "/paypal.png" },
    { name: "googlePay", url: "/google-pay.png" },
    { name: "mercadopago", url: "/mercado.png" },
    { name: "applePay", url: "/pay.png" },
  ];
  const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KY);

  const handleBuy = async () => {
    try {
      setLoading(true);

      // Llama al backend para obtener el init_point
      const info = {
        title: "Inversión en proyecto",
        unit_price: parseFloat(newInvestment.monto),
        quantity: 1,
        nombre: user.name,
        external_reference: user.datos_empresas[0]._id,
        payerEmail: user.email,
      }
      const response = await processPayment(info);

      if (response.init_point) {
        // Redirige al usuario al checkout de Mercado Pago
        window.location.href = response.init_point;
      } else {
        alert("No se pudo obtener el enlace de pago.");
      }
    } catch (error) {
      console.error("Error al generar preferencia:", error);
      alert("Ocurrió un error al iniciar el pago.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form>
      <Grid size={12}>
        <Typography className="titleMain" variant="h3" component="h2">
          Monto a invertir
        </Typography>
        <Typography className="subtitleMain" mt={1}>
          Elige el monto que deseas invertir y completa los detalles para finalizar tu participación.
        </Typography>
      </Grid>
      <Grid size={12} mt={5} mb={3}>
        <TextField
          className="input-sigup"
          error={isEmpty(errors.monto) ? false : true}
          required
          fullWidth
          id="monto"
          label="Ingresa el monto de inversión"
          name="monto"
          value={newInvestment.monto}
          placeholder="$XXX"
          onChange={(e) => handleChange("monto", e.target.value)}
          helperText={!isEmpty(errors.monto) && errors.monto}
          type="number"
          min={0}
        />
      </Grid>

      <Grid size={12} mt={5}>
        <Typography className="titleMain" variant="h3" component="h2">Método de pago</Typography>
        <Typography className="subtitleMain" mt={1}>Selecciona un método de pago.</Typography>
      </Grid>
      <Stack
        direction="row"
        useFlexGap
        sx={{ flexWrap: 'wrap' }}
        spacing={2}
        mt={3}
      >
        {PAY_OPTIONS.map((option, index) => (
          <Button
            key={`option${index}`}
            variant={newInvestment.type == option.name ? "contained" : "outlined"}
            onClick={() => handleChange("type", option.name)}
            color="info"
          >
            <img alt={`Icon ${option.name}`} src={option.url} width={60} style={{ margin: 3 }} />
          </Button>
        ))}
      </Stack>
      <Box sx={{ textAlign: 'center', mt: 4 }}>
        {
          newInvestment.type === 'paypal' ? (
            <PayPalScriptProvider
              options={{
                'client-id': process.env.REACT_APP_PAYPAL_CLIENT_ID,
                currency: 'MXN',
                locale: 'es_MX',
              }}
            >
              <PayPalButtons
                disabled={newInvestment.monto <= 0}
                createOrder={(data, actions) => {
                  return actions.order.create({
                    intent: 'CAPTURE',
                    purchase_units: [
                      {
                        amount: {
                          currency_code: 'MXN', // Cambia a 'USD' si es necesario
                          value: newInvestment.monto.toString(), // Asegúrate de que sea un string
                        },
                        custom_id: user._id // Agregamos ID de usuario para el webhook
                      },
                    ],
                  });
                }}
                onApprove={async (data, actions) => {
                  try {
                    const orderData = await actions.order.capture();
                    const transaction = orderData.purchase_units[0].payments.captures[0];
                    const info = {
                      monto: transaction.amount.value,
                      recibo: transaction.id,
                      metodo: 'paypal'
                    };

                    if (transaction.status === 'COMPLETED') {
                      info.status = 1;
                      const emailData = {
                        subject: 'aprobacion',
                        email: user.email,
                        nombre: user.name
                      };
                      await addInvestment(info, user._id, user.datos_empresas[0]._id);
                      await sendEmailApproval(emailData);
                      const result = MODAL_MESSAGES.SUCCESS_INVERSION;
                      setModalContent(result);
                      setShowModal(true);
                    } else if (transaction.status === 'PENDING') {
                      await addInvestment(info, user._id, user.datos_empresas[0]._id);
                      const result = MODAL_MESSAGES.PENDING_INVERSION;
                      setModalContent(result);
                      setShowModal(true);
                    } else {
                      const result = MODAL_MESSAGES.ERROR_INVERSION;
                      setModalContent(result);
                      setShowModal(true);
                    }
                  } catch (error) {
                    const result = MODAL_MESSAGES.ERROR_INVERSION;
                    setModalContent(result);
                    setShowModal(true);
                  }
                }}
                onError={(err) => {
                  const result = MODAL_MESSAGES.ERROR_INVERSION;
                  setModalContent(result);
                  setShowModal(true);
                }}
              />
            </PayPalScriptProvider>
          ) : newInvestment.type === 'mercadopago' ? (
            <Button
              onClick={handleBuy}
              type='button'
              disabled={newInvestment.monto <= 0}
              sx={{
                height: "57px",
                backgroundColor: "#00A1E4",
                color: "white",
                padding: "10px 20px",
                borderRadius: "5px",
                display: "flex",
                gap: "10px",
              }}
            >
              <img
                src="https://logospng.org/download/mercado-pago/logo-mercado-pago-icone-1024.png"
                alt="Mercado Pago"
                style={{ width: "24px", height: "24px" }}
              />
              Pagar con Mercado Pago
            </Button>

          ) : newInvestment.type === 'googlePay' ? (
            <GooglePayButton
              addInvestment={addInvestment}
              sendEmailApproval={sendEmailApproval}
              user={user}
              setModalContent={setModalContent}
              setShowModal={setShowModal}
              newInvestment={newInvestment}
              disabled={newInvestment.monto <= 10}
            />
          ) : newInvestment.type === 'applePay' ? (
            <Elements stripe={stripePromise}>
              <ApplePayButton
                user={user}
                newInvestment={newInvestment}
                addInvestment={addInvestment}
                sendEmailApproval={sendEmailApproval}
                setModalContent={setModalContent}
                setShowModal={setShowModal}
                setLoading={setLoading}
              />
            </Elements>
          ) : null
        }
      </Box>
    </form>
  )
}

FormInversion.propTypes = {
  user: PropTypes.object.isRequired,
  setShowModal: PropTypes.func.isRequired,
  setModalContent: PropTypes.func.isRequired,
  addInvestment: PropTypes.func.isRequired,
  sendEmailApproval: PropTypes.func.isRequired,
  setLoading: PropTypes.func.isRequired,
}

export default FormInversion;